'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Language, Mode } from '@/lib/api';
import { modeLabel as fillMode } from '@/lib/modeText';
import {
  copyEntry, createFolder, deleteEntry, deleteFolder, liveNotebook,
  loadLastFolder, saveLastFolder, updateEntry, updateFolder,
  type NotebookEntry, type NotebookFolder, type NotebookRow, type NotebookSource, type NotebookTree,
} from '@/lib/notebook';
import TranslationReplica from '@/components/TranslationReplica';
import { useWait } from '@/lib/wait';
import FolderTree, { buildTree, ENTRY_DRAG, FolderGlyph, folderIcon, type TreeNode } from './FolderTree';
import Layer from '../Layer';

/**
 * The Notebook — everything this account has ever translated, in folders.
 *
 * ── Why it is a dialog and not a page ───────────────────────────────────────
 * Because the thing it is FOR is the sentence in the box behind it. Somebody
 * opens the notebook to find the phrasing they used last week and put it back
 * into the pane they are typing in, and a route change would throw that pane
 * away — the draft, the half-finished answer, the target language they picked.
 * At 95% of the viewport it is a full screen in every way that matters, and it
 * closes onto the work it interrupted rather than onto a page load.
 *
 * ── The three columns ───────────────────────────────────────────────────────
 *   folders │ entries │ the exchange itself
 *
 * They are the three questions in order — where, which, what — and on a wide
 * screen all three answers stay on screen at once, so picking a different entry
 * is one click rather than a click and a Back. Below `lg` there is only room
 * for one, so the dialog shows one at a time and the header grows a back
 * arrow; `pane` is that state and it is ignored entirely on a wide screen.
 *
 * ── Nothing here translates ─────────────────────────────────────────────────
 * The viewer is `TranslationReplica`, the same inert replica the admin console
 * uses. The one write it offers is **Load into translator**, which copies the
 * entry back into the panes and closes the dialog — an explicit button, because
 * the alternative (clicking an entry loads it) would make browsing your own
 * archive destructive to whatever you had in the box.
 */

export default function NotebookDialog({
  onClose,
  onLoad,
  modes,
  languages,
  styleLabels,
  /**
   * The mode the translator behind the dialog is in.
   *
   * It decides which folder the notebook opens onto: the one that mode's work
   * is filed in. See `loadTree`.
   */
  mode,
  /**
   * Whose notebook this is, and where its rows come from.
   *
   * Defaults to the signed-in account's. The admin console passes a source
   * bound to another account and `readOnly`, which is the whole of the
   * difference between the two screens — see lib/notebook.ts.
   */
  source,
  readOnly = false,
  owner,
  /**
   * Open onto this entry.
   *
   * It arrives LATE as often as it arrives with the dialog: the rail button
   * saves and opens in one gesture, and the id only exists once the server has
   * answered — a beat after the notebook is already on screen. So this is
   * watched rather than merely seeded, and the list is re-read when it changes,
   * because the entry that has just been created was not in the page of rows
   * this dialog fetched on the way up.
   */
  initialEntryId,
}: {
  onClose: () => void;
  /** Absent in a read-only view: there is no translator to load anything into. */
  onLoad?: (entry: NotebookEntry) => void;
  modes: Mode[];
  languages: Language[];
  styleLabels: Record<string, string>;
  mode?: string | null;
  source?: NotebookSource;
  readOnly?: boolean;
  /** Named in the header when this is somebody else's notebook. */
  owner?: string | null;
  initialEntryId?: string | null;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const src = source ?? liveNotebook;

  const [tree, setTree] = useState<NotebookTree | null>(null);
  const [folderId, setFolderId] = useState<string | null>(null);
  const [rows, setRows] = useState<NotebookRow[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [entry, setEntry] = useState<NotebookEntry | null>(null);
  const [openId, setOpenId] = useState<string | null>(initialEntryId ?? null);

  const [q, setQ] = useState('');
  const [listBusy, setListBusy] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pane, setPane] = useState<'folders' | 'list' | 'entry'>('list');
  // Opening: the folders have not arrived yet, so the dialog is an empty frame.
  useWait(tree === null && !error, 'Opening your notebook');
  /** Bumped to re-read the current list — see `initialEntryId`. */
  const [reload, setReload] = useState(0);

  /**
   * Is the folder sidebar squeezed to a rail?
   *
   * Remembered per browser, because it is a statement about how somebody works
   * rather than about what they are looking at: a reader with one folder wants
   * the width back permanently, and asking them to collapse it again on every
   * visit would be the app forgetting a preference it was told twice.
   *
   * Read in an effect rather than in `useState`, like every other preference in
   * this app — the server-rendered HTML has no localStorage, and seeding state
   * from it directly is a hydration mismatch.
   */
  const [sidebarShut, setSidebarShut] = useState(false);
  useEffect(() => {
    try {
      setSidebarShut(localStorage.getItem(SIDEBAR_KEY) === '1');
    } catch {
      /* Storage disabled. The sidebar opens, which is the safe default. */
    }
  }, []);
  const toggleSidebar = useCallback(() => {
    setSidebarShut((v) => {
      try { localStorage.setItem(SIDEBAR_KEY, v ? '0' : '1'); } catch { /* ignore */ }
      return !v;
    });
  }, []);

  /*
   * Collapsing only means anything where the three columns exist.
   *
   * Below `lg` the dialog shows ONE column at a time (see `pane`), so the
   * folders pane is already the whole width — a 52px rail floating in it would
   * be a control that had broken rather than one that had been put away. The
   * state is kept either way, so widening the window gives back the rail the
   * reader chose rather than a sidebar they have to shut again.
   */
  const wide = useWide();
  const railed = sidebarShut && wide;
  const [confirmFolder, setConfirmFolder] = useState<NotebookFolder | null>(null);
  /** An entry waiting for somewhere to go — "Move to…" and "Copy to…" both. */
  /*
   * An entry waiting for somewhere to go.
   *
   * The id and its folder rather than the row itself, because both the LIST and
   * the open ENTRY can ask for this now and they are different shapes — a row
   * carries snippets, an entry carries the whole text. Everything the picker
   * needs is these four fields.
   */
  const [picking, setPicking] = useState<
    { id: string; folderId: string; label: string; action: 'move' | 'copy' } | null
  >(null);

  // ── The tree ───────────────────────────────────────────────────────────────

  const loadTree = useCallback(async (select?: boolean) => {
    try {
      const t = await src.tree();
      setTree(t);
      /*
       * Land on the folder for the mode the translator is in, and on History
       * when that mode has none.
       *
       * An empty middle column with "pick a folder" written in it is a dialog
       * that opens onto a chore — and since every mode files into its own
       * folder, the mode the reader was just working in is the folder holding
       * what they were just doing. History is the fallback and no longer the
       * default: it now holds only what has nowhere else to go, so opening onto
       * it would often mean opening onto an empty list.
       */
      if (select) {
        /*
         * The mode's folder, or History.
         *
         * With a translator behind the dialog there is a current mode, and that
         * mode's folder is what the reader was just working in. Without one —
         * the console, opening somebody else's notebook — History is the right
         * landing: it lists the whole notebook, newest first, which is the only
         * honest answer to "show me this person's notebook" when nothing is
         * known about what they were doing.
         */
        const byMode = t.folders.find((f) => f.kind === 'mode' && f.mode === mode)?.id;
        /*
         * In the user's own notebook there are no mode folders (the personal view
         * returns only the folders they made), so land on the one they last kept
         * something in — the work usually continues where it left off — and fall
         * back to the first folder, or to nothing when the notebook is still empty.
         */
        const last = readOnly ? null : (() => { const l = loadLastFolder(); return l && t.folders.some((f) => f.id === l) ? l : null; })();
        setFolderId((cur) => cur ?? byMode ?? last ?? t.historyId ?? t.folders[0]?.id ?? null);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, [mode, src, readOnly]);

  useEffect(() => { void loadTree(true); }, [loadTree]);

  // ── The list ───────────────────────────────────────────────────────────────

  /*
   * Search is debounced and folder changes are not.
   *
   * A folder click is one deliberate act and should feel like one; a search is
   * a letter at a time, and a request per letter would put four requests in
   * flight for a three-letter word and paint them in whatever order they came
   * back. 250ms is short enough to feel live over a local API.
   */
  useEffect(() => {
    let alive = true;
    const run = async () => {
      setListBusy(true);
      try {
        const r = await src.entries({ folderId, q: q.trim() });
        if (!alive) return;
        setRows(r.items);
        setNextBefore(r.nextBefore);
      } catch (err) {
        if (alive) setError((err as Error).message);
      } finally {
        if (alive) setListBusy(false);
      }
    };
    const t = setTimeout(run, q.trim() ? 250 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [folderId, q, reload, src]);

  const loadMore = async () => {
    if (!nextBefore) return;
    setBusy(true);
    try {
      const r = await src.entries({ folderId, q: q.trim(), before: nextBefore });
      setRows((prev) => [...prev, ...r.items]);
      setNextBefore(r.nextBefore);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // ── The open entry ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!openId) { setEntry(null); return; }
    let alive = true;
    void src.entry(openId)
      .then((r) => { if (alive) setEntry(r.entry); })
      .catch((err) => { if (alive) setError((err as Error).message); });
    return () => { alive = false; };
  }, [openId, src]);

  /*
   * Opened on a specific entry (the toast's "Open"): the list has to follow it
   * to the folder it is in, or the middle column sits on History showing
   * nothing to do with what is on the right.
   */
  useEffect(() => {
    if (!initialEntryId) return;
    setOpenId(initialEntryId);
    // The row is new, so the list on screen predates it, and the counts in the
    // sidebar are one behind. Both are re-read rather than patched in — the
    // server has just decided things this component cannot infer, such as
    // whether the save landed on a row that already existed.
    void loadTree();
    setReload((v) => v + 1);
  }, [initialEntryId, loadTree]);

  useEffect(() => {
    if (initialEntryId && entry && entry.id === initialEntryId) {
      setFolderId(entry.folderId);
      setPane('entry');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.id]);

  // ── Keys and the scroll lock ───────────────────────────────────────────────

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      /*
       * Escape backs OUT one step before it closes.
       *
       * A dialog that shuts on the first Escape whatever is inside it is one
       * that punishes the key: somebody closing a confirmation loses the whole
       * notebook with it and has to find their place again.
       */
      if (confirmFolder) { setConfirmFolder(null); return; }
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    panel.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, confirmFolder]);

  // ── Writes ─────────────────────────────────────────────────────────────────

  const guard = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    await loadTree();
    const r = await src.entries({ folderId, q: q.trim() });
    setRows(r.items);
    setNextBefore(r.nextBefore);
  };

  const onCreate = (name: string, parentId: string | null) =>
    guard(async () => {
      const { folder } = await createFolder(name, parentId);
      await loadTree();
      setFolderId(folder.id);
      saveLastFolder(folder.id);
    });

  const onRename = (id: string, name: string) =>
    guard(async () => {
      if (!name.trim()) return;
      await updateFolder(id, { name: name.trim() });
      await loadTree();
    });

  const onMoveFolder = (id: string, parentId: string | null) =>
    guard(async () => {
      await updateFolder(id, { parentId });
      await loadTree();
    });

  const onDropEntry = (entryId: string, target: string) =>
    guard(async () => {
      await updateEntry(entryId, { folderId: target });
      await refresh();
    });

  const removeFolder = (folder: NotebookFolder, purge: boolean) =>
    guard(async () => {
      await deleteFolder(folder.id, purge);
      setConfirmFolder(null);
      const t = await src.tree();
      setTree(t);
      if (folderId === folder.id) setFolderId(t.historyId);
      else await refresh();
    });

  const removeEntry = (id: string) =>
    guard(async () => {
      await deleteEntry(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      if (openId === id) { setOpenId(null); setPane('list'); }
      await loadTree();
    });

  const star = (row: NotebookRow) =>
    guard(async () => {
      const { entry: saved } = await updateEntry(row.id, { starred: !row.starred });
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, starred: saved.starred } : r)));
      if (entry?.id === row.id) setEntry((e) => (e ? { ...e, starred: saved.starred } : e));
    });

  /**
   * Move or copy, once a destination has been chosen.
   *
   * Move is a folder change on the row that exists; copy makes a second one.
   * Both land the reader in the destination folder afterwards, because the
   * question they will have next is "did it get there", and answering it by
   * showing them is cheaper than a message that says it did.
   */
  const place = (id: string, action: 'move' | 'copy', target: string) =>
    guard(async () => {
      if (action === 'move') {
        await updateEntry(id, { folderId: target });
        /*
         * The open entry has to be re-read, not just the list.
         *
         * The viewer prints the folder its entry is in, off the copy it fetched
         * when it was opened — so moving an entry while looking at it left the
         * header still naming the folder it had just left. Everything else on
         * screen had moved and that one line had not, which reads as the move
         * having half-failed.
         */
        if (openId === id) {
          const { entry: fresh } = await src.entry(id);
          setEntry(fresh);
        }
      } else {
        /*
         * A copy leaves the original where it was and makes a new row — so the
         * thing to show afterwards is the COPY. Following the original into a
         * folder it is not in would put the reader in Archive looking at the
         * entry that is still in History, with the list and the pane disagreeing
         * about what they are showing.
         */
        const { entry: made } = await copyEntry(id, target);
        setOpenId(made.id);
      }
      setPicking(null);
      saveLastFolder(target);
      setQ('');
      setFolderId(target);
      await loadTree();
    });

  const rename = (id: string, title: string) =>
    guard(async () => {
      const { entry: saved } = await updateEntry(id, { title });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, title: saved.title } : r)));
      setEntry((e) => (e && e.id === id ? { ...e, title: saved.title } : e));
    });

  const folderName = (id: string | null) =>
    tree?.folders.find((f) => f.id === id)?.name ?? 'Notebook';

  /**
   * Is the open folder History — the one that lists the WHOLE notebook?
   *
   * It is a view rather than a place (see the list route in the backend's
   * routes/notebook.ts): its rows come from every folder, so the list has to
   * name the folder each one is actually in, exactly as a search does.
   */
  const inHistory = !!tree?.folders.find((f) => f.id === folderId && f.kind === 'history');

  const langName = (code: string) =>
    languages.find((l) => l.code === code)?.name ?? code;

  const searching = !!q.trim();

  return (
    <Layer>
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-2 backdrop-blur-sm sm:p-4"
      // Only a click on the backdrop itself closes: a drag that starts on the
      // text and ends outside must not close the dialog mid-selection.
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Notebook"
    >
      <div
        ref={panel}
        tabIndex={-1}
        className="flex h-[95vh] w-[95vw] flex-col overflow-hidden rounded-2xl border border-ink-200
                   bg-white shadow-2xl outline-none dark:border-ink-700 dark:bg-ink-950"
      >
        {/* ── Chrome ───────────────────────────────────────────────────────── */}
        <header className="flex shrink-0 items-center gap-3 border-b border-ink-200 px-3 py-2.5 sm:px-4 dark:border-ink-700">
          {/* The back arrow exists only where the columns do not fit. */}
          {pane !== 'folders' && (
            <button
              type="button"
              onClick={() => setPane(pane === 'entry' ? 'list' : 'folders')}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-100
                         lg:hidden dark:text-ink-300 dark:hover:bg-ink-800"
              aria-label="Back"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 6l-6 6 6 6" />
              </svg>
            </button>
          )}

          <h2 className="flex items-center gap-2 text-[15px] font-semibold text-ink-900 dark:text-ink-50">
            <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] text-accent-600 dark:text-accent-400" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
              <path d="M6.5 3.5h11a1 1 0 011 1v15a1 1 0 01-1 1h-11a2 2 0 01-2-2v-13a2 2 0 012-2z" />
              <path d="M4.5 8.5h3M4.5 12h3M4.5 15.5h3" strokeLinecap="round" />
            </svg>
            Notebook
          </h2>

          {/* Whose, when it is not yours. The console opens this over somebody
              else's archive, and a dialog that looks exactly like your own
              notebook must say whose it is where the eye lands first. */}
          {owner && (
            <span className="max-w-[40%] truncate rounded-full bg-accent-600/10 px-2 py-0.5 text-xs
                             font-medium text-accent-700 dark:bg-accent-400/15 dark:text-accent-200">
              {owner}
            </span>
          )}

          <span className="hidden text-xs text-ink-400 sm:inline">
            {tree ? `${tree.total.toLocaleString()} saved` : '…'}
            {readOnly && ' · read-only'}
          </span>

          <div className="relative ml-auto w-40 sm:w-72">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              /*
               * Escape clears the search first and closes the dialog only when
               * there is nothing to clear — the order every search box people
               * already use has, and the one that stops a half-typed query from
               * costing somebody the whole notebook.
               */
              onKeyDown={(e) => {
                if (e.key === 'Escape' && q) { e.stopPropagation(); setQ(''); }
              }}
              placeholder="Search everything…"
              className="w-full rounded-lg border border-ink-200 bg-ink-100/60 px-3 py-1.5 pr-8 text-[13px]
                         outline-none transition focus:border-accent-500 focus:bg-white
                         dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50 dark:focus:bg-ink-900"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ('')}
                aria-label="Clear search"
                className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center
                           rounded text-ink-400 hover:text-ink-700 dark:hover:text-ink-100"
              >
                ✕
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close the notebook"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink-500
                       transition hover:bg-ink-100 hover:text-ink-900 dark:text-ink-300 dark:hover:bg-ink-800 dark:hover:text-ink-50"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        {error && (
          <p className="shrink-0 border-b border-red-200 bg-red-50 px-4 py-2 text-[13px] text-red-700
                        dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300" role="status">
            {error}
          </p>
        )}

        {/* ── The three columns ────────────────────────────────────────────── */}
        <div
          className={
            'grid min-h-0 flex-1 grid-cols-1 ' +
            // Both templates written out in full: Tailwind reads the source for
            // class names, and a string it has to compute is a class it never
            // generates.
            (railed ? 'lg:grid-cols-[52px_340px_1fr]' : 'lg:grid-cols-[250px_340px_1fr]')
          }
        >
          {/* Folders */}
          <div
            className={
              'min-h-0 border-ink-200 lg:block lg:border-r dark:border-ink-700 ' +
              (pane === 'folders' ? 'block' : 'hidden')
            }
          >
            <FolderTree
              folders={tree?.folders ?? []}
              counts={tree?.counts ?? {}}
              selected={searching ? null : folderId}
              onSelect={(id) => {
                setFolderId(id);
                // Remembered for the SAVE chord, which only the owner's own view
                // has — the console must not rewrite the operator's own
                // preference by browsing somebody else's tree.
                if (!readOnly) saveLastFolder(id);
                setPane('list');
                setQ('');
              }}
              onCreate={onCreate}
              onRename={onRename}
              onDelete={(f) => setConfirmFolder(f)}
              onMoveFolder={onMoveFolder}
              onDropEntry={onDropEntry}
              collapsed={railed}
              onToggleCollapsed={toggleSidebar}
              busy={busy}
              modes={modes}
              readOnly={readOnly}
            />
          </div>

          {/* Entries */}
          <div
            className={
              'flex min-h-0 flex-col border-ink-200 lg:flex lg:border-r dark:border-ink-700 ' +
              (pane === 'list' ? 'flex' : 'hidden')
            }
          >
            <div className="shrink-0 border-b border-ink-200 px-3 py-2 dark:border-ink-700">
              <p className="flex items-center gap-2 truncate text-[12px] font-semibold uppercase tracking-wider text-ink-400">
                {searching ? `Results for “${q.trim()}”` : folderName(folderId)}
                {/* Said out loud, because the same folder used to show only what
                    was filed IN it: this list is the whole notebook, not the
                    History folder's own rows. */}
                {!searching && inHistory && (
                  <span className="rounded-full bg-ink-200/70 px-1.5 py-0.5 text-[10px] font-medium
                                   normal-case tracking-normal text-ink-500 dark:bg-ink-700/70 dark:text-ink-300">
                    everything, every folder
                  </span>
                )}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {listBusy && rows.length === 0 && (
                <p className="p-4 text-[13px] text-ink-400">Looking…</p>
              )}
              {!listBusy && rows.length === 0 && (
                <p className="p-4 text-[13px] leading-relaxed text-ink-400">
                  {searching
                    ? 'Nothing in the notebook matches that.'
                    : emptyLine(tree?.folders.find((f) => f.id === folderId) ?? null)}
                </p>
              )}

              <ul>
                {rows.map((r) => (
                  <EntryRow
                    key={r.id}
                    row={r}
                    open={openId === r.id}
                    modeName={fillMode(modes.find((m) => m.id === r.mode)?.label ?? r.mode, langName(r.sourceLang))}
                    folder={searching || inHistory ? folderName(r.folderId) : null}
                    onOpen={() => { setOpenId(r.id); setPane('entry'); }}
                    onStar={() => star(r)}
                    onRename={(title) => rename(r.id, title)}
                    onMove={() => setPicking({
                      id: r.id, folderId: r.folderId, action: 'move',
                      label: r.title || r.overview || r.targetSnippet,
                    })}
                    onCopy={() => setPicking({
                      id: r.id, folderId: r.folderId, action: 'copy',
                      label: r.title || r.overview || r.targetSnippet,
                    })}
                    onDelete={() => removeEntry(r.id)}
                    readOnly={readOnly}
                  />
                ))}
              </ul>

              {nextBefore && (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={busy}
                  className="w-full py-3 text-[13px] font-medium text-accent-600 hover:bg-ink-100
                             disabled:opacity-50 dark:text-accent-300 dark:hover:bg-ink-800"
                >
                  {busy ? 'Loading…' : 'Load more'}
                </button>
              )}
            </div>
          </div>

          {/* The exchange */}
          <div
            className={
              'min-h-0 overflow-y-auto bg-ink-100/50 p-3 sm:p-5 lg:block dark:bg-ink-900/40 ' +
              (pane === 'entry' ? 'block' : 'hidden')
            }
          >
            {!entry ? (
              <div className="flex h-full items-center justify-center p-6 text-center">
                <p className="max-w-xs text-[13px] leading-relaxed text-ink-400">
                  Pick something on the left and it opens here exactly as it was on screen —
                  both panes, the mode, the languages and every tab of the answer.
                </p>
              </div>
            ) : (
              <EntryView
                entry={entry}
                modes={modes}
                languages={languages}
                styleLabels={styleLabels}
                folderName={folderName(entry.folderId)}
                langName={langName}
                onRename={(title) => rename(entry.id, title)}
                onLoad={onLoad ? () => onLoad(entry) : undefined}
                onMove={() => setPicking({
                  id: entry.id, folderId: entry.folderId, action: 'move',
                  label: entry.title || entry.sourceText.slice(0, 120),
                })}
                onCopy={() => setPicking({
                  id: entry.id, folderId: entry.folderId, action: 'copy',
                  label: entry.title || entry.sourceText.slice(0, 120),
                })}
                onDelete={() => removeEntry(entry.id)}
                busy={busy}
                readOnly={readOnly}
              />
            )}
          </div>
        </div>
      </div>

      {picking && (
        <FolderPicker
          folders={tree?.folders ?? []}
          counts={tree?.counts ?? {}}
          modes={modes}
          title={picking.action === 'move' ? 'Move to folder' : 'Copy to folder'}
          subject={picking.label}
          current={picking.folderId}
          action={picking.action}
          busy={busy}
          onCancel={() => setPicking(null)}
          onPick={(id) => place(picking.id, picking.action, id)}
          onCreate={async (name) => {
            const { folder } = await createFolder(name, null);
            await loadTree();
            return folder.id;
          }}
        />
      )}

      {confirmFolder && (
        <DeleteFolderConfirm
          folder={confirmFolder}
          count={tree?.counts[confirmFolder.id] ?? 0}
          onCancel={() => setConfirmFolder(null)}
          onConfirm={(purge) => removeFolder(confirmFolder, purge)}
          busy={busy}
        />
      )}
    </div>
    </Layer>
  );
}

// ── The list row ─────────────────────────────────────────────────────────────

/**
 * One entry in the middle column.
 *
 * Draggable, because the drop target is right there in the next column over and
 * dragging is what a person reaches for when a tree is on screen. The row also
 * keeps a star and a delete under the pointer — a menu for two actions would be
 * a click to reach either.
 */
function EntryRow({
  row,
  open,
  modeName,
  folder,
  onOpen,
  onStar,
  onRename,
  onMove,
  onCopy,
  onDelete,
  readOnly,
}: {
  row: NotebookRow;
  open: boolean;
  modeName: string;
  /** Named only while searching, when the results span the whole notebook. */
  folder: string | null;
  onOpen: () => void;
  onStar: () => void;
  onRename: (title: string) => void;
  onMove: () => void;
  onCopy: () => void;
  onDelete: () => void;
  /** A view of somebody else's notebook. Nothing here writes; see the dialog. */
  readOnly?: boolean;
}) {
  const [renaming, setRenaming] = useState(false);
  const [menu, setMenu] = useState(false);

  /*
   * What the row is CALLED.
   *
   * The title, then the summary, then the first thing the answer said. An entry
   * whose title has been cleared is not nameless — the overview names it, which
   * is the same sentence that named it before anybody typed over it. "Untitled"
   * is the last resort and, with the overview in hand, almost never reached.
   */
  const name = row.title || row.overview || row.targetSnippet || 'Untitled';

  return (
    <li
      draggable={!readOnly}
      onDragStart={(e) => {
        if (readOnly) return;
        e.dataTransfer.setData(ENTRY_DRAG, row.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      className={
        'group relative cursor-pointer border-b border-ink-200/70 px-3 py-2.5 transition-colors ' +
        'dark:border-ink-800 ' +
        (open
          ? 'bg-accent-600/10 dark:bg-accent-400/10'
          : 'hover:bg-ink-100 dark:hover:bg-ink-800/60')
      }
      onClick={onOpen}
    >
      <div className="flex items-start gap-2">
        {renaming ? (
          /* Renaming happens IN the row, not in a dialog. It is one line of
             text; a modal to change it would be three clicks for a word. The
             click guard stops the row underneath from opening the entry while
             somebody is aiming at the caret. */
          <input
            autoFocus
            defaultValue={row.title || row.overview || ''}
            onClick={(e) => e.stopPropagation()}
            onBlur={(e) => { onRename(e.target.value.trim()); setRenaming(false); }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') { onRename(e.currentTarget.value.trim()); setRenaming(false); }
              // Escape abandons the rename and must not reach the document, or
              // it closes the whole notebook. Same rule as the folder inputs.
              if (e.key === 'Escape') { e.stopPropagation(); setRenaming(false); }
            }}
            className="min-w-0 flex-1 rounded border border-accent-500 bg-white px-1.5 py-0.5 text-[13px]
                       outline-none dark:bg-ink-900 dark:text-ink-50"
          />
        ) : (
          <p className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink-900 dark:text-ink-50">
            {row.starred && <span className="mr-1 text-amber-500">★</span>}
            {name}
          </p>
        )}
        <span className="shrink-0 whitespace-nowrap text-[11px] text-ink-400">{relTime(row.lastSeenAt)}</span>
      </div>

      <p className="mt-0.5 truncate text-[12px] text-ink-500 dark:text-ink-400">{row.targetSnippet}</p>

      <div className="mt-1 flex items-center gap-1.5 text-[10.5px] text-ink-400">
        <span className="rounded-full bg-ink-200/70 px-1.5 py-0.5 font-medium dark:bg-ink-700/70">
          {modeName}
        </span>
        <span>{row.sourceLang} → {row.targetLang}</span>
        {folder && <span className="truncate">· {folder}</span>}
        {/* The "recorded automatically" mark belongs to the console's record of a
            user's activity, never to the user's own private notebook — a personal
            space should not tell its owner anything is being logged. So it shows
            only in the read-only (admin) view. */}
        {readOnly && row.auto && <span title="Recorded automatically">· auto</span>}
      </div>

      {/* Star stays on the row — it is the one action people use often enough
          that a menu would be in the way. Everything else is behind the `⋯`, so
          the quiet row is a title and a line of the answer. `stopPropagation`
          on both, because the whole row is the open button. */}
      {!readOnly && (
      <span className={'absolute right-2 top-2 items-center gap-0.5 ' + (menu ? 'flex' : 'hidden group-hover:flex')}>
        <IconButton label={row.starred ? 'Unstar' : 'Star'} onClick={(e) => { e.stopPropagation(); onStar(); }}>
          <span className={row.starred ? 'text-amber-500' : ''}>★</span>
        </IconButton>
        <IconButton label="More" onClick={(e) => { e.stopPropagation(); setMenu((v) => !v); }}>
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor">
            <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
          </svg>
        </IconButton>
      </span>
      )}

      {menu && (
        <>
          <span className="fixed inset-0 z-20" onClick={(e) => { e.stopPropagation(); setMenu(false); }} />
          <span
            className="absolute right-2 top-9 z-30 flex w-44 flex-col overflow-hidden rounded-lg border
                       border-ink-200 bg-white py-1 shadow-xl dark:border-ink-700 dark:bg-ink-900"
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem label="Rename" onClick={() => { setMenu(false); setRenaming(true); }} />
            {/* Move and copy, as two separate items rather than one dialog with a
                radio in it. They are different intentions and naming both is
                cheaper to read than a control that asks which you meant. */}
            <MenuItem label="Move to folder…" onClick={() => { setMenu(false); onMove(); }} />
            <MenuItem label="Copy to folder…" onClick={() => { setMenu(false); onCopy(); }} />
            <MenuItem label="Delete" danger onClick={() => { setMenu(false); onDelete(); }} />
          </span>
        </>
      )}
    </li>
  );
}

function MenuItem({ label, onClick, danger }: { label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'px-3 py-1.5 text-left text-[13px] transition ' +
        (danger
          ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10'
          : 'text-ink-700 hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-ink-800')
      }
    >
      {label}
    </button>
  );
}

function IconButton({
  label,
  children,
  onClick,
  danger,
}: {
  label: string;
  children: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={
        'flex h-6 w-6 items-center justify-center rounded bg-white/90 text-[12px] shadow-sm transition ' +
        'dark:bg-ink-900/90 ' +
        (danger
          ? 'text-ink-400 hover:text-red-600 dark:hover:text-red-400'
          : 'text-ink-400 hover:text-ink-900 dark:hover:text-ink-50')
      }
    >
      {children}
    </button>
  );
}

// ── The viewer ───────────────────────────────────────────────────────────────

function EntryView({
  entry,
  modes,
  languages,
  styleLabels,
  folderName,
  langName,
  onRename,
  onLoad,
  onMove,
  onCopy,
  onDelete,
  busy,
  readOnly,
}: {
  entry: NotebookEntry;
  modes: Mode[];
  languages: Language[];
  styleLabels: Record<string, string>;
  folderName: string;
  langName: (code: string) => string;
  onRename: (title: string) => void;
  /** Absent in a read-only view — there is no translator to load into. */
  onLoad?: () => void;
  onMove: () => void;
  onCopy: () => void;
  onDelete: () => void;
  busy: boolean;
  readOnly?: boolean;
}) {
  const [title, setTitle] = useState(entry.title);
  useEffect(() => setTitle(entry.title), [entry.id, entry.title]);
  const [menu, setMenu] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  // A new entry is a new menu. Without this, opening one with the `⋯` still
  // down leaves it hanging over a different exchange's header.
  useEffect(() => setMenu(false), [entry.id]);

  return (
    /*
     * The full width of the column, not the translator's reading measure.
     *
     * The replica caps itself at `max-w-4xl` because that is right on the
     * TRANSLATOR: it is one page with a header above it and prose below, and a
     * line of text that runs the whole of a wide monitor is a line nobody
     * finishes. In here the replica IS the page — it already sits in the third
     * of three columns, and capping it again left a 95%-wide dialog drawing its
     * panes down the middle with empty grey either side, which is the exact
     * complaint that produced this change. The column is the measure.
     */
    <div className="flex w-full flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* Read-only: the title is a heading, not a field. An input that refuses
            to save is worse than plain text — it invites the edit and then
            loses it. */}
        {readOnly ? (
          <h3 className="min-w-0 flex-1 px-2 py-1 text-[15px] font-semibold text-ink-900 dark:text-ink-50">
            {entry.title || 'Untitled'}
          </h3>
        ) : (
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => { if (title !== entry.title) onRename(title); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            // Escape puts the old title back and stays in the notebook. Letting
            // it reach the document would close the dialog over an abandoned
            // edit, which is the one moment it is most annoying to lose.
            if (e.key === 'Escape') { e.stopPropagation(); setTitle(entry.title); e.currentTarget.blur(); }
          }}
          // `basis-full` until there is room for it: at phone width the title
          // shared a row with two buttons and was squeezed to about a dozen
          // characters, so an entry called "Contract clause 4 — payment terms"
          // read as "Contract cla". It takes its own line instead, and the row
          // is already `flex-wrap`, so this is the whole of the fix.
          className="min-w-0 flex-1 basis-full rounded-lg border border-transparent bg-transparent
                     px-2 py-1 text-[15px] font-semibold text-ink-900 outline-none transition
                     hover:border-ink-200 focus:border-accent-500 focus:bg-white sm:basis-auto
                     dark:text-ink-50 dark:hover:border-ink-700 dark:focus:bg-ink-900"
          aria-label="Entry title"
          ref={titleRef}
        />
        )}
        {onLoad && (
          <button
            type="button"
            onClick={onLoad}
            disabled={busy}
            className="rounded-lg bg-accent-600 px-3 py-1.5 text-[13px] font-medium text-white
                       transition hover:bg-accent-700 disabled:opacity-50"
          >
            Load into translator
          </button>
        )}

        {/*
          The same `⋯` the list rows carry, in the pane's top-right corner.
          
          Everything you can do TO an entry now lives in one menu with one set
          of words, reachable from wherever you happen to be looking at it — the
          list on the left or the exchange on the right. It used to be that the
          list had rename, move and copy while the open entry had only delete,
          so finding the move you wanted meant going back to a row you had
          already left.

          Delete moved INTO the menu rather than staying beside this button.
          Keeping both would put it on screen twice, and the loud outlined
          version of it sat next to the primary action — the most dangerous
          thing in the pane, at the size of the safest.
        */}
        {!readOnly && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenu((v) => !v)}
            disabled={busy}
            aria-label="More actions"
            aria-expanded={menu}
            title="More actions"
            className={
              'flex h-[30px] w-[30px] items-center justify-center rounded-lg border transition ' +
              'disabled:opacity-50 ' +
              (menu
                ? 'border-ink-300 bg-ink-100 text-ink-900 dark:border-ink-600 dark:bg-ink-800 dark:text-ink-50'
                : 'border-ink-200 text-ink-500 hover:bg-ink-100 hover:text-ink-900 ' +
                  'dark:border-ink-700 dark:text-ink-300 dark:hover:bg-ink-800 dark:hover:text-ink-50')
            }
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
              <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
            </svg>
          </button>

          {menu && (
            <>
              {/* The click-away. A menu that only closes on its own items is a
                  menu you have to use to be rid of. */}
              <span className="fixed inset-0 z-20" onClick={() => setMenu(false)} />
              <div
                className="gt-pop-in absolute right-0 top-9 z-30 flex w-48 flex-col overflow-hidden
                           rounded-lg border border-ink-200 bg-white py-1 shadow-xl
                           dark:border-ink-700 dark:bg-ink-900"
              >
                {/* Rename puts the caret in the title above rather than opening
                    anything: the field is already there and already editable,
                    and a dialog to change one line would be the longer way round
                    to the same input. */}
                <MenuItem
                  label="Rename"
                  onClick={() => {
                    setMenu(false);
                    const el = titleRef.current;
                    el?.focus();
                    el?.setSelectionRange(0, el.value.length);
                  }}
                />
                <MenuItem label="Move to folder…" onClick={() => { setMenu(false); onMove(); }} />
                <MenuItem label="Copy to folder…" onClick={() => { setMenu(false); onCopy(); }} />
                <MenuItem label="Delete" danger onClick={() => { setMenu(false); onDelete(); }} />
              </div>
            </>
          )}
        </div>
        )}
      </div>

      <p className="px-2 text-[11px] text-ink-400">
        {folderName} · {new Date(entry.lastSeenAt).toLocaleString()}
        {/* Both dates, but only when they are different — History updates a row
            rather than adding a second one, so "last translated" and "first
            filed" genuinely diverge, and printing the same date twice on every
            other entry would be noise. */}
        {Math.abs(new Date(entry.lastSeenAt).getTime() - new Date(entry.createdAt).getTime()) > 60000 &&
          ` (first saved ${new Date(entry.createdAt).toLocaleDateString()})`}
        {entry.auto ? ' · recorded automatically' : readOnly ? ' · kept by hand' : ' · saved by you'}
        {entry.model ? ` · ${entry.model}` : ''}
      </p>

      <TranslationReplica
        // See the note on the wrapper above: in the notebook the replica has a
        // column of its own and should fill it.
        maxWidth="max-w-none"
        row={{
          mode: entry.mode,
          sourceText: entry.sourceText,
          sourceLang: entry.sourceLang,
          sourceLangName: langName(entry.sourceLang),
          sourceLangDetected: entry.sourceLangDetected,
          targetText: entry.targetText,
          targetLang: entry.targetLang,
          targetLangName: langName(entry.targetLang),
          ms: entry.ms,
          cached: entry.cached,
          tabs: entry.tabs,
          activeTab: entry.activeTab,
        }}
        modes={modes}
        languages={languages}
        styleLabels={styleLabels}
      />
    </div>
  );
}

// ── Choosing a destination ───────────────────────────────────────────────────

/**
 * Where should this go?
 *
 * ── What the first version got wrong ────────────────────────────────────────
 * It was a list of folder names as plain text, each with a number beside it,
 * and nothing else: no glyphs, no sense of which folder sat inside which, no
 * sign that a row was even clickable until you hit it, and a "Cancel" floating
 * under everything with no bar to sit on. It read as a debug list rather than
 * as part of the product, which matters here more than almost anywhere — this
 * dialog is the one moment the notebook asks the reader to make a decision
 * about their own filing.
 *
 * ── What it does now ────────────────────────────────────────────────────────
 *  • **The tree keeps its shape.** Rows are indented by depth with a hairline
 *    guide, so "Client A inside Work" looks like it is inside Work. The flat
 *    "Work / Client A" string it used before made the reader parse the
 *    hierarchy out of punctuation.
 *  • **A row says what it will do, on hover.** "Move here →" appears in the
 *    row under the pointer. A list where every line looks inert until clicked
 *    is a list people click cautiously; naming the action in place removes the
 *    question.
 *  • **It is drivable from the keyboard.** ↑ ↓ move the highlight, Enter
 *    picks, Escape cancels, and typing filters. The highlight and the hover are
 *    the same visual state, so the two ways of using it agree.
 *  • **The folder it is already in is shown, disabled, marked.** Hiding it
 *    would leave the most familiar name missing from the list, which reads as
 *    the list being wrong.
 *  • **It can make the folder on the spot.** Somebody moving an entry very
 *    often discovers at that exact moment that the place they want does not
 *    exist yet, and being sent back to the sidebar means abandoning the move.
 */
function FolderPicker({
  folders,
  counts,
  modes,
  title,
  subject,
  current,
  action,
  busy,
  onCancel,
  onPick,
  onCreate,
}: {
  folders: NotebookFolder[];
  counts: Record<string, number>;
  /** For the capture folders' icons — the picker draws the sidebar's rows. */
  modes: Mode[];
  title: string;
  subject: string;
  current: string;
  action: 'move' | 'copy';
  busy: boolean;
  onCancel: () => void;
  onPick: (folderId: string) => void;
  onCreate: (name: string) => Promise<string>;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [making, setMaking] = useState(false);
  const [filter, setFilter] = useState('');
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  /** The tree flattened for display, each row keeping the depth it sits at. */
  type Row = { id: string; name: string; path: string; depth: number; kind: string; icon: string | null };
  const flat = useMemo<Row[]>(() => {
    const walk = (nodes: TreeNode[], depth = 0, prefix = ''): Row[] =>
      nodes.flatMap((n) => {
        const path = prefix ? `${prefix} / ${n.folder.name}` : n.folder.name;
        return [
          {
            id: n.folder.id,
            name: n.folder.name,
            path,
            depth,
            kind: n.folder.kind,
            icon: folderIcon(n.folder, modes),
          },
          ...walk(n.children, depth + 1, path),
        ];
      });
    return walk(buildTree(folders));
  }, [folders, modes]);

  /*
   * Filtering matches the whole PATH, not the folder's own name.
   *
   * Typing "work" should find "Work / Client A" as well as "Work" — somebody
   * narrowing a list is thinking about where a thing goes, and the parent is
   * part of that thought. Matching rows keep their indentation, so a child
   * whose parent has been filtered out still reads as a child rather than
   * jumping to the left.
   */
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? flat.filter((f) => f.path.toLowerCase().includes(q)) : flat;
  }, [flat, filter]);

  /** Where the keyboard can actually land. The current folder is not a choice. */
  const pickable = shown.filter((f) => f.id !== current);

  useEffect(() => { setCursor(0); }, [filter]);

  const make = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || making) return;
    setMaking(true);
    try {
      onPick(await onCreate(trimmed));
    } finally {
      setMaking(false);
    }
  }, [name, making, onCreate, onPick]);

  /*
   * The keys, on the panel rather than the document.
   *
   * The notebook behind this is listening for Escape on the document, so a key
   * handled here must not carry on and close it too — every branch below stops
   * the event. It is also why the panel takes focus on open: a dialog whose
   * keys only work once you have clicked something is a dialog the keyboard
   * cannot reach at all.
   */
  const onKey = (e: React.KeyboardEvent) => {
    if (adding) {
      if (e.key === 'Escape') { e.stopPropagation(); setAdding(false); setName(''); }
      return;
    }
    if (e.key === 'Escape') { e.stopPropagation(); onCancel(); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      setCursor((c) => {
        const n = pickable.length;
        if (!n) return 0;
        return (c + (e.key === 'ArrowDown' ? 1 : -1) + n) % n;
      });
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const target = pickable[cursor];
      if (target) onPick(target.id);
      return;
    }
    // Typing filters. Single characters only, so the chords above still work.
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.stopPropagation();
      setFilter((f) => f + e.key);
      return;
    }
    if (e.key === 'Backspace') { e.stopPropagation(); setFilter((f) => f.slice(0, -1)); }
  };

  const verb = action === 'move' ? 'Move here' : 'Copy here';

  return (
    <Layer>
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/55 p-4 backdrop-blur-[2px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        ref={(el) => el?.focus()}
        onKeyDown={onKey}
        className="gt-pop-in flex max-h-[76vh] w-full max-w-md flex-col overflow-hidden rounded-2xl
                   border border-ink-200 bg-white shadow-2xl outline-none
                   dark:border-ink-700 dark:bg-ink-900"
      >
        {/* ── Header: what is about to happen, and to what ─────────────────── */}
        <div className="flex shrink-0 items-start gap-3 border-b border-ink-200 px-5 py-4 dark:border-ink-700">
          {/* The action as a mark. Move is an arrow going into a folder; copy is
              two sheets. It is the fastest way to tell the two dialogs apart,
              and they are otherwise identical. */}
          <span
            aria-hidden
            className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl
                       bg-accent-600/10 text-accent-600 dark:bg-accent-400/15 dark:text-accent-300"
          >
            {action === 'move' ? (
              <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3.5 7.5a1 1 0 011-1h3.5l2 2h9a1 1 0 011 1v8a1 1 0 01-1 1h-6" />
                <path d="M12 14.5H4M7.5 11l-3.5 3.5L7.5 18" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="11" height="11" rx="1.6" />
                <path d="M15 6.5V5.4a1 1 0 00-1-1H4.6a1 1 0 00-1 1V14a1 1 0 001 1h1.1" />
              </svg>
            )}
          </span>

          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-semibold text-ink-900 dark:text-ink-50">{title}</h3>
            {/* Two lines and then an ellipsis, not one line cut mid-word: this is
                the only thing on screen saying WHICH entry is being moved, and a
                title clipped after four words does not identify it. */}
            <p className="mt-1 line-clamp-2 text-[12.5px] leading-snug text-ink-500 dark:text-ink-400">
              {subject}
            </p>
          </div>

          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel"
            className="-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-ink-400
                       transition hover:bg-ink-100 hover:text-ink-900 dark:hover:bg-ink-800 dark:hover:text-ink-50"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {/* Type-to-filter, shown only when there is enough to filter. Below that
            it is a control taking up room to solve a problem nobody has. */}
        {flat.length > 6 && (
          <div className="shrink-0 border-b border-ink-200 px-3 py-2 dark:border-ink-700">
            <div className="flex items-center gap-2 rounded-lg bg-ink-100 px-2.5 py-1.5 dark:bg-ink-950">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-ink-400" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
                <circle cx="11" cy="11" r="6" /><path d="M20 20l-4.5-4.5" />
              </svg>
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={(e) => {
                  // The panel owns ↑ ↓ and Enter even while the caret is here.
                  if (['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
                  if (e.key === 'Escape') { e.stopPropagation(); setFilter(''); }
                }}
                placeholder="Filter folders…"
                className="w-full bg-transparent text-[13px] outline-none placeholder:text-ink-400
                           dark:text-ink-50"
              />
            </div>
          </div>
        )}

        {/* ── The folders ──────────────────────────────────────────────────── */}
        <ul ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-2">
          {shown.length === 0 && (
            <li className="px-3 py-6 text-center text-[13px] text-ink-400">
              No folder matches “{filter.trim()}”.
            </li>
          )}

          {shown.map((f) => {
            const here = f.id === current;
            const at = pickable.indexOf(f);
            const on = !here && at === cursor;
            return (
              <li key={f.id}>
                <button
                  type="button"
                  disabled={here || busy}
                  onMouseEnter={() => { if (!here) setCursor(at); }}
                  onClick={() => onPick(f.id)}
                  aria-current={on ? 'true' : undefined}
                  style={{ paddingLeft: 10 + f.depth * 16 }}
                  className={
                    'group flex w-full items-center gap-2.5 rounded-lg py-2 pr-2.5 text-left ' +
                    'transition-colors ' +
                    (here
                      ? 'cursor-default text-ink-400'
                      : on
                        ? 'bg-accent-600/10 text-ink-900 dark:bg-accent-400/15 dark:text-ink-50'
                        : 'text-ink-700 dark:text-ink-200')
                  }
                >
                  {/* The guide, so depth is visible and not merely implied by a
                      gap. Only on nested rows — a line beside a top-level folder
                      would suggest a parent it does not have. */}
                  {f.depth > 0 && (
                    <span aria-hidden className="-ml-2 h-4 w-2 shrink-0 border-l border-ink-200 dark:border-ink-700" />
                  )}

                  <span className={here ? 'opacity-50' : ''}>
                    <FolderGlyph history={f.kind === 'history'} open={false} icon={f.icon} label={f.name} />
                  </span>

                  <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium">{f.name}</span>

                  {here ? (
                    <span className="shrink-0 rounded-full bg-ink-100 px-2 py-0.5 text-[10.5px] font-medium
                                     text-ink-500 dark:bg-ink-800 dark:text-ink-400">
                      {action === 'move' ? 'currently here' : 'the original'}
                    </span>
                  ) : (
                    <>
                      {/* The count, and — under the pointer — what clicking does
                          instead. They swap rather than sit side by side: the
                          row is 40px and two pieces of chrome in the same corner
                          is how the first version got cluttered. */}
                      <span
                        className={
                          'shrink-0 tabular-nums text-[11px] text-ink-400 ' +
                          (on ? 'hidden' : 'inline')
                        }
                      >
                        {counts[f.id] || ''}
                      </span>
                      <span
                        className={
                          'shrink-0 items-center gap-1 rounded-full bg-accent-600 px-2 py-0.5 ' +
                          'text-[10.5px] font-semibold text-white ' +
                          (on ? 'inline-flex' : 'hidden')
                        }
                      >
                        {verb}
                        <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h13M13 6l6 6-6 6" />
                        </svg>
                      </span>
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        {/* ── The bar. Two real buttons on a surface, not links in space ────── */}
        <div className="shrink-0 border-t border-ink-200 bg-ink-100/50 p-2.5 dark:border-ink-700 dark:bg-ink-950/50">
          {adding ? (
            <div className="flex items-center gap-2">
              <span className="pl-1 text-ink-400" aria-hidden>
                <FolderGlyph history={false} open={false} />
              </span>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name the new folder"
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') void make();
                  if (e.key === 'Escape') { setAdding(false); setName(''); }
                }}
                className="min-w-0 flex-1 rounded-lg border border-accent-500 bg-white px-2.5 py-1.5
                           text-[13px] outline-none dark:bg-ink-900 dark:text-ink-50"
              />
              <button
                type="button"
                onClick={() => void make()}
                disabled={!name.trim() || making}
                className="shrink-0 rounded-lg bg-accent-600 px-3 py-1.5 text-[13px] font-semibold text-white
                           transition hover:bg-accent-700 disabled:opacity-40"
              >
                {making ? '…' : action === 'move' ? 'Move' : 'Copy'}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium
                           text-ink-600 transition hover:bg-ink-200/60 hover:text-accent-600
                           dark:text-ink-300 dark:hover:bg-ink-800 dark:hover:text-accent-300"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                New folder
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg border border-ink-200 bg-white px-3.5 py-1.5 text-[13px] font-medium
                           text-ink-600 transition hover:bg-ink-100 hover:text-ink-900
                           dark:border-ink-700 dark:bg-ink-900 dark:text-ink-300
                           dark:hover:bg-ink-800 dark:hover:text-ink-50"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </Layer>
  );
}

// ── Deleting a folder ────────────────────────────────────────────────────────

/**
 * The one destructive action in the notebook, asked properly.
 *
 * Two buttons rather than one, because "delete this folder" means two very
 * different things and only the user knows which: the entries move to History,
 * or they go with it. Moving is the default and the primary button; deleting
 * says how many are about to go, because a number is the difference between a
 * warning and a formality.
 */
function DeleteFolderConfirm({
  folder,
  count,
  onCancel,
  onConfirm,
  busy,
}: {
  folder: NotebookFolder;
  count: number;
  onCancel: () => void;
  onConfirm: (purge: boolean) => void;
  busy: boolean;
}) {
  return (
    <Layer>
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm rounded-2xl border border-ink-200 bg-white p-5 shadow-2xl dark:border-ink-700 dark:bg-ink-900">
        <h3 className="text-[15px] font-semibold text-ink-900 dark:text-ink-50">
          Delete “{folder.name}”?
        </h3>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-600 dark:text-ink-300">
          {count > 0
            ? `It holds ${count.toLocaleString()} ${count === 1 ? 'entry' : 'entries'}. Any folders inside it go too.`
            : 'It is empty. Any folders inside it go too.'}
        </p>
        <div className="mt-4 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => onConfirm(false)}
            disabled={busy}
            className="rounded-lg bg-accent-600 px-3 py-2 text-[13px] font-medium text-white
                       transition hover:bg-accent-700 disabled:opacity-50"
          >
            Delete the folder, keep the entries in History
          </button>
          <button
            type="button"
            onClick={() => onConfirm(true)}
            disabled={busy}
            className="rounded-lg border border-red-300 px-3 py-2 text-[13px] font-medium text-red-600
                       transition hover:bg-red-50 disabled:opacity-50
                       dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10"
          >
            Delete the folder and everything in it
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-2 text-[13px] font-medium text-ink-500 transition
                       hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
    </Layer>
  );
}

/** Where the sidebar's own state is kept. Per browser — see `sidebarShut`. */
/**
 * What an empty folder says.
 *
 * Three different sentences, because "Nothing here yet" leaves three different
 * questions open. In a mode's folder the answer is that the folder fills itself
 * the moment you use that mode; in History it is that this folder is now the
 * one for leftovers rather than the one everything lands in; in a folder the
 * user made it is how to put something in it. One sentence covering all three
 * — which is what was here before the mode folders existed — is now wrong in
 * two of them: it told somebody standing in an empty Translate folder that
 * their work was filed in History.
 */
function emptyLine(folder: NotebookFolder | null): string {
  if (folder?.kind === 'mode') {
    return `Nothing here yet. Everything you translate in ${folder.name} is filed here on its own — nothing to save.`;
  }
  if (folder?.kind === 'history') {
    // Empty History now means an empty NOTEBOOK — it lists every folder's rows —
    // so the line is about the notebook rather than about this folder.
    return 'Nothing yet. Everything you translate is recorded here, and filed in its mode’s folder above.';
  }
  if (!folder) {
    // No folder selected — which, in the personal view, means the notebook has no
    // folders yet. Point at the one move that gets it started.
    return 'Your notebook is empty. Make a folder in the sidebar, then keep a translation in it with the notebook button on the mode rail (Ctrl+7).';
  }
  return 'Nothing here yet. Keep what is on screen with the notebook button on the mode rail (Ctrl+7), or drag an entry in from another folder.';
}

const SIDEBAR_KEY = 'gt.notebook.sidebar';

/**
 * Is there room for the three columns?
 *
 * 1024px is Tailwind's `lg`, which is where the grid switches — the number is
 * repeated here rather than derived because there is nowhere to derive it from,
 * and the two must not drift. Starts false so the first client paint agrees
 * with the server's, which has no window to measure.
 */
function useWide(): boolean {
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const read = () => setWide(mq.matches);
    read();
    mq.addEventListener('change', read);
    return () => mq.removeEventListener('change', read);
  }, []);
  return wide;
}

/** "3 min ago". A notebook is read in relative time; the exact date is in the viewer. */
function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 31) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}
